<template>
  <div class="brand-edit">
    <!-- Brand Header -->
    <h4 class="text-primary text-center mb-2">
      <i class="fa-solid fa-bullhorn"></i>
      {{ $t('tabs.brands.details') }}
    </h4>

    <!-- General Info -->
    <b-card class="mb-1">
      <b-card-title>{{ $t('columns.generalInfo') }}</b-card-title>
      <b-row>
        <b-col cols="12" md="3">
          <label>{{ $t('columns.name') }}</label>
          <b-form-input v-model="brand.name" />
        </b-col>


        <b-col cols="12" md="6">
          <label>{{ $t('columns.countries') }}</label>
          <b-form-group>
            <v-select
                v-model="selectedCountries"
                :options="availableCountries"
                multiple
                label="name"
                :reduce="country => country.code"
                class="w-100"
                placeholder="Select countries"
                @input="onCountriesChange"
            />
          </b-form-group>
        </b-col>


        <b-col cols="2" xl="2">
          <label class="invisible">{{ $t('columns.active') }}</label>
          <b-checkbox switch v-model="brand.active">{{ $t('columns.active') }}</b-checkbox>
        </b-col>

      </b-row>
    </b-card>

    <!-- Geo-specific Opening Times -->
    <div v-for="(geo, gIndex) in brand.geos" :key="geo.countryCode" class="mb-3">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">{{ geo.countryName }}</h5>
          <b-button size="sm" variant="outline-primary" @click="copyFromPrevious(gIndex)" v-if="gIndex > 0">
            <i class="fa fa-copy mr-1"></i>{{ $t('operations.copyPrevious') }}
          </b-button>
        </div>



        <b-table-simple bordered responsive>
          <b-thead>
            <b-tr>
              <b-th>{{ $t('day') }}</b-th>
              <b-th v-for="day in weekdays" :key="day">{{ day }}</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <!-- Enabled -->
            <b-tr>
              <b-td>{{ $t('enabled') }}</b-td>
              <b-td v-for="(day, index) in weekdays" :key="geo.countryCode+'-enabled-'+day">
                <b-form-checkbox v-model="geo.openingTimes[index].enabled" switch></b-form-checkbox>
              </b-td>
            </b-tr>

            <!-- Start Time -->
            <b-tr>
              <b-td>{{ $t('startTime') }}</b-td>
              <b-td v-for="(day, index) in weekdays" :key="geo.countryCode+'-start-'+day">
                <b-form-timepicker
                    v-model="geo.openingTimes[index].startTime"
                    value-as-string
                    locale="en"
                    :disabled="!geo.openingTimes[index].enabled"
                />
              </b-td>
            </b-tr>

            <!-- End Time -->
            <b-tr>
              <b-td>{{ $t('endTime') }}</b-td>
              <b-td v-for="(day, index) in weekdays" :key="geo.countryCode+'-end-'+day">
                <b-form-timepicker
                    v-model="geo.openingTimes[index].endTime"
                    value-as-string
                    locale="en"
                    :disabled="!geo.openingTimes[index].enabled"
                />
              </b-td>
            </b-tr>

            <!-- Max Leads -->
            <b-tr>
              <b-td>{{ $t('maxLeads') }}</b-td>
              <b-td v-for="(day, index) in weekdays" :key="geo.countryCode+'-maxLeads-'+day">
                <b-form-input
                    type="number"
                    min="0"
                    v-model.number="geo.openingTimes[index].maxLeads"
                    :disabled="!geo.openingTimes[index].enabled"
                />
              </b-td>
            </b-tr>

            <!-- Max FTDs -->
            <b-tr>
              <b-td>{{ $t('maxFtds') }}</b-td>
              <b-td v-for="(day, index) in weekdays" :key="geo.countryCode+'-maxFtds-'+day">
                <b-form-input
                    type="number"
                    min="0"
                    v-model.number="geo.openingTimes[index].maxFtds"
                    :disabled="!geo.openingTimes[index].enabled"
                />
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>
    </div>

    <!-- Actions -->
    <div class="text-center mt-2">
      <b-button size="sm" variant="outline-success" @click="save">{{ $t('operations.save') }}</b-button>
    </div>
  </div>
</template>

<script>
import Brand from '@/model/brand/Brand'
import { mapActions } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'

export default {
  name: 'BrandEdit',
  mixins: [mixinNotifications],
  data() {
    return {
      brand: Brand(),
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],

      availableCountries: [
        { code: 'FR', name: 'France' },
        { code: 'ES', name: 'Spain' },
        { code: 'DE', name: 'Germany' },
        { code: 'IT', name: 'Italy' },
        { code: 'US', name: 'United States' },
        { code: 'IL', name: 'Israel' }
      ],
      selectedCountries: []
    }
  },
  props: {
    value: { type: Object, default: () => ({}) }
  },
  watch: {
    value: {
      handler(val) {
        this.brand = Object.assign(Brand(), val)

        if (!Array.isArray(this.brand.geos)) {
          this.brand.geos = []
        }

        // 🔑 sync the select model with geos from the brand
        this.selectedCountries = this.brand.geos.map(g => g.countryCode)
      },
      immediate: true
    }
  }

  ,
  methods: {
    ...mapActions('brand', ['createBrand', 'updateBrand']),
    onCountriesChange(countries) {
      if (!Array.isArray(this.brand.geos)) {
        this.$set(this.brand, 'geos', [])
      }

      this.brand.geos = countries.map(code => {
        const country = this.availableCountries.find(c => c.code === code)
        const existing = this.brand.geos.find(g => g.countryCode === code) // now safe
        return existing || {
          countryCode: country.code,
          countryName: country.name,
          openingTimes: this.weekdays.map(day => ({
            weekday: day.toUpperCase(),
            enabled: false,
            startTime: '09:00',
            endTime: '17:00',
            maxLeads: null,
            maxFtds: null
          }))
        }
      })
    }
    ,
    copyFromPrevious(index) {
      if (index > 0) {
        this.$set(this.brand.geos, index, {
          ...this.brand.geos[index],
          openingTimes: JSON.parse(JSON.stringify(this.brand.geos[index - 1].openingTimes))
        })
        this.showSuccessNotification(this.$t('globalMessages.copySuccess'))
      }
    },
    async save() {
      if (!this.brand.id) {
        await this.createBrand({ brand: this.brand })
      } else {
        await this.updateBrand({ brand: this.brand, id: this.brand.id })
      }
      this.showSuccessNotification(this.$t('globalMessages.updateDataSuccess', { system: this.$t('menu.brands') }))
      this.$emit('saved', this.brand)
      this.refreshTables();
      this.$emit('input', this.campaign)
      this.$emit('close')
    },
    reset() {
      this.brand = Brand()
      this.selectedCountries = []
    }
  }
}
</script>
